<template>
  <div class="liveness-container">
    <!-- Intro screen -->
    <div v-if="showIntro" class="intro-center" role="region" aria-label="Liveness pre-check">
      <div class="intro-card">
        <div class="intro-icon" aria-hidden="true"><img :src="require('@/assets/images/centered_face.svg')"></div>
        <h1 class="intro-title">Let's make sure it's really you.</h1>
        <p class="intro-subtitle">Before you start:</p>
        <ul class="intro-list">
          <li class="intro-list-item">
            <span class="intro-list-icon" aria-hidden="true"><img :src="require('@/assets/images/lighting.svg')"></span>
            <span class="intro-list-text">It's best to be in a well-lit room.</span>
          </li>
          <li class="intro-list-item">
            <span class="intro-list-icon" aria-hidden="true"><img :src="require('@/assets/images/no_accessories.svg')"></span>
            <span class="intro-list-text">Remove accessories from your face.</span>
          </li>
          <li class="intro-list-item">
            <span class="intro-list-icon" aria-hidden="true"><img :src="require('@/assets/images/face_visible.svg')"></span>
            <span class="intro-list-text">Ensure your face is fully visible.</span>
          </li>
          <li class="intro-list-item">
            <span class="intro-list-icon" aria-hidden="true"><img :src="require('@/assets/images/move_gently.svg')"></span>
            <span class="intro-list-text">Move your head slightly — avoid sudden or fast movements.</span>
          </li>
        </ul>
        <p class="intro-volume-note">🔊 Please turn the volume up.</p>
        <button type="button" class="btn btn-primary intro-cta" aria-label="Start liveness" @click="startLivenessFlow">
          Start liveness
        </button>
      </div>
    </div>

    <!-- UI: Centered circular camera preview layout -->
    <div v-else class="liveness-center">

      <!-- UI: Unified instruction prompt area above camera -->
      <div class="instruction-prompt-area">
        <!-- Main instruction prompt -->
        <div v-if="currentGestureInstruction && isRecording && !preStartPositioning && !faceOutOfPosition" class="instruction-prompt">
          {{ currentGestureInstruction }}
        </div>
        <div v-else-if="preStartPositioning && isRecording" class="instruction-prompt">
          {{ statusText }}
        </div>
        <div v-else-if="faceOutOfPosition && isRecording" class="instruction-prompt error">
          Face out of position — please return to center
        </div>
        <div v-else-if="startingLivenessMessage" class="instruction-prompt success">
          {{ startingLivenessMessage }}
        </div>
        <div v-else-if="gestureIgnoredWarning" class="instruction-prompt error">
          {{ gestureIgnoredWarning }}
        </div>
      </div>

      <!-- UI: Circular camera preview mask with dynamic border feedback -->
      <div class="camera-circle-wrapper">
        <div
          class="camera-circle"
          :class="{
            'border-perfect': (faceCentered && faceDistance === 'perfect') && isRecording && !faceOutOfPosition && dynamicValidationActive,
            'border-error': faceOutOfPosition || (!faceDetected && isRecording && dynamicValidationActive),
            'border-neutral': isRecording && (preStartPositioning || isInitializing)
          }"
        >
          <!-- Live Camera Feed (only shown when liveness is not completed) -->
          <video
            v-show="!showCanvas && !livenessCompleted"
            ref="videoElement"
            class="video-preview"
            :class="{ 'mirrored': !videoBlob }"
            autoplay
            playsinline
            muted
          ></video>

          <!-- Canvas for MediaPipe Overlay (only shown when liveness is not completed) -->
          <canvas
            v-show="showCanvas && !livenessCompleted"
            ref="canvasElement"
            class="video-preview"
            :class="{ 'mirrored': !videoBlob }"
          ></canvas>

          <!-- Post-Liveness Result State -->
          <div
            v-if="livenessCompleted"
            class="liveness-result"
            :class="{ 'success': livenessSuccess, 'failure': !livenessSuccess }"
          >
            <!-- Success: Show captured face image -->
            <img
              v-if="livenessSuccess && capturedImageUrl"
              :src="capturedImageUrl"
              alt="Captured Face"
              class="cropped-face"
            />
            <!-- Failure: Black overlay (no content needed, CSS handles it) -->
          </div>

          <!-- UI: Loader overlay during upload -->
          <div v-if="isInitializing || (isLivenessPassed && !postLivenessHandled)" class="loader-overlay">
            <div class="modern-spinner"></div>
            <p class="loader-text">{{ isInitializing ? 'Initializing camera...' : 'Processing your liveness check...' }}</p>
          </div>

          <!-- Success checkmark -->
          <div v-if="isLivenessPassed && postLivenessHandled" class="success-checkmark">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="28" fill="#00c853" />
              <path d="M15 30 L25 40 L45 20" stroke="white" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>

        <!-- Gesture progress indicator (small badges) -->
        <div v-if="isRecording && faceDetected && selectedGestures.length > 0" class="gesture-dots">
          <span
            v-for="(gesture, index) in selectedGestures"
            :key="index"
            class="dot"
            :class="{ 'active': index === currentGestureIndex, 'completed': gesture.done }"
          ></span>
        </div>
      </div>

      <!-- UI: Progress bar below camera -->
      <div v-if="isRecording && !isLivenessPassed" class="progress-bar-wrapper">
        <div class="progress-bar">
          <div class="progress" :style="{ width: progress + '%' }"></div>
        </div>
        <p class="progress-text">{{ Math.max(0, maxRecordingTime - timer) }}s</p>
      </div>

      <!-- Status message -->
      <p v-if="!isRecording && !videoBlob && !isLivenessPassed" class="status-message">
        Position your face in the circle and click Start
      </p>
      <p v-else-if="isLivenessPassed && postLivenessHandled" class="status-message success">
        ✓ Liveness verification successful!
      </p>
      <p v-else-if="livenessValidationFailed" class="status-message error">
        Liveness check failed — face was not properly centered. Please retry.
      </p>
      <p v-else-if="timedOut" class="status-message error">
        ⏱ Liveness check timed out. Please try again.
      </p>
      <p v-else-if="showRetry" class="status-message error">
        {{ statusText }}
      </p>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          v-if="!isRecording && !videoBlob && !isLivenessPassed"
          @click="startRecording"
          :disabled="isInitializing || !cameraReady"
          class="btn btn-primary"
        >
          Start Liveness Check
        </button>

        <button
          v-if="(videoBlob && !isLivenessPassed) || showRetry"
          @click="retry"
          class="btn btn-secondary"
        >
          Retry
        </button>

        <button
          v-if="isLivenessPassed && !showRetry && postLivenessHandled"
          @click="submitVideo"
          class="btn btn-primary"
        >
          Continue
        </button>

        <button
          v-if="isLivenessPassed && !showRetry && postLivenessHandled"
          @click="retry"
          class="btn btn-secondary"
        >
          Re-record
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * RecordVideo.vue
 *
 * A Vue 2 component that performs client-side liveness detection using MediaPipe FaceMesh.
 * Records a video during the liveness check and emits the blob on successful verification.
 *
 * Features:
 * - Real-time face detection using MediaPipe
 * - Head movement detection using facial landmarks
 * - Video recording with MediaRecorder API
 * - iPhone/Safari compatibility with rotation handling
 * - Smooth UX with progress indicators
 * - Post-liveness photo capture and upload
 *
 * Ported from origin/direct_prod's src/views/deep-register-customer/RecordVideo.vue,
 * with the deep-link draft save/resume logic stripped out (not applicable to this flow)
 * and this view's own navigation / analytics preserved.
 */

import request from '@/utils/request'

export default {
  name: 'RecordVideo',

  data() {
    return {
      // Intro state
      showIntro: true,

      // Status & UI States
      statusText: 'Initializing camera...',
      statusClass: '',
      isInitializing: true,
      isRecording: false,
      isLivenessPassed: false,
      cameraReady: false,
      showCanvas: false,

      // Post-Liveness Handling
      postLivenessHandled: false,
      showRetry: false,

      // Video & Recording
      videoBlob: null,
      mediaRecorder: null,
      recordedChunks: [],
      stream: null,
      mirrorCanvas: null, // Canvas for mirrored recording
      mirrorCanvasContext: null,
      mirrorStream: null, // Stream from mirrored canvas
      supportedMimeType: '', // Detected MIME type for this browser

      // Face Centering & Quality
      faceCentered: true,
      facePositionMessage: '',
      showCenteringGuide: true, // Show visual guide for face alignment

      // Pre-Start Positioning & Dynamic Validation
      preStartPositioning: false, // True when waiting for proper face position before starting
      perfectPositionReached: false, // True when both centering and distance are correct
      startingLivenessMessage: '', // "Perfect! Starting liveness check..."
      timerPaused: false, // True when timer is paused due to face out of position
      faceOutOfPosition: false, // True when face moves out of acceptable range during session
      faceLostTimeout: null, // Timeout for face lost detection
      faceLostDuration: 0, // How long face has been lost (in seconds)
      maxFaceLostDuration: 5, // Maximum seconds before stopping session
      dynamicValidationActive: false, // True when checking position during gestures
      faceCenteringTolerance: 0.15, // ±15% tolerance for centering

      // Real-time position validation for gesture checks
      isFaceInValidPosition: false, // True only when face is centered AND at correct distance
      gestureIgnoredWarning: '', // Warning message when gesture is ignored
      gestureIgnoredTimeout: null, // Timeout for warning message

      // Final Validation - track time face was in valid position
      validPositionTime: 0, // Seconds face was in valid position
      totalRecordingTime: 0, // Total recording time (excluding paused time)
      minValidPositionRatio: 0.8, // Minimum 80% of time must be in valid position
      livenessValidationFailed: false, // True if final validation fails

      // Post-Liveness UI State
      livenessCompleted: false, // True when liveness check is finished (success or failure)
      livenessSuccess: false, // True if liveness succeeded
      capturedImageUrl: null, // URL for the captured face image (success state)

      // Audio Feedback
      successAudio: null, // Audio instance for gesture success feedback
      voiceInstructions: {}, // Audio instances for voice instructions (smile, turn left, turn right, blink, passed, failed)

      // Face Distance Feedback (Mobile-friendly)
      faceDistance: 'unknown', // 'too-close', 'too-far', 'perfect', 'unknown'
      faceDistanceMessage: '',
      showDistanceFeedback: true,

      // Timer & Progress
      timer: 0,
      progress: 0,
      timerInterval: null,
      maxRecordingTime: 15, // seconds - limit for file size management
      timedOut: false, // Track if liveness check timed out

      // Face Detection
      faceDetected: false,
      faceMesh: null,
      faceMeshLoaded: false,
      detectionInterval: null,
      lastDetectionTime: 0,
      videoFrameCallbackId: null,
      rafId: null,

      // Gesture-Based Liveness Checks
      availableGestures: [
        { name: 'smile', label: 'Smile at the camera', done: false },
        { name: 'turnLeft', label: 'Look to your left', done: false },
        { name: 'turnRight', label: 'Look to your right', done: false }
      ],
      selectedGestures: [], // Two randomly selected gestures for this session
      currentGestureIndex: 0,
      currentGestureName: '',
      currentGestureInstruction: '',
      gestureTimeout: 15, // seconds per gesture
      gestureTimer: 0,
      gestureTimerInterval: null,

      // Smile Detection - Baseline + Delta Approach
      smileBaseline: null, // Neutral mouth ratio captured at session start (normalized)
      smileThresholdDelta: 0.05, // Delta above baseline to detect smile (relative, tuneable)
      SMILE_HOLD_MS: 300, // Required continuous smile time in milliseconds
      SMILE_FRAMES_COUNT: 6, // Required consecutive frames above threshold
      SMILE_COOLDOWN_MS: 1000, // Cooldown period - do not accept another smile within this time
      smileStartTime: null, // Timestamp when smile first detected above threshold
      smileFrameCount: 0, // Consecutive frames above threshold counter
      lastSmileTime: 0, // Timestamp of last accepted smile
      smileRatiosBuffer: [], // Ring buffer of recent ratios for stability check
      SMILE_BUFFER_LEN: 8, // Length of buffer used to compute variance for stability

      // Head Movement Tracking with Rolling Buffer
      headPositionBuffer: [], // Buffer to store recent head positions
      bufferSize: 5, // Number of frames to average
      previousNoseX: null,
      previousNoseY: null,
      neutralNoseX: null, // Neutral/center X position (normalized 0-1)
      neutralNoseY: null, // Neutral/center Y position (normalized 0-1)
      faceStableFrames: 0, // Counter to ensure face is stable before setting neutral
      faceStableThreshold: 15, // Frames needed to consider face stable (~1 second at 15fps)
      neutralPositionSet: false, // Flag to track if neutral position has been calibrated

      // Head Turn Detection Thresholds (normalized coordinates)
      headTurnThresholdNormalized: 0.12, // 3% deviation from neutral for natural head turn
      headTurnConfirmationFrames: 0, // Counter for consecutive detection frames
      requiredConfirmationFrames: 4, // Need 4 consecutive frames to confirm gesture
      turnLeftStart: null, // Timestamp when left turn first detected
      turnRightStart: null, // Timestamp when right turn first detected
      headTurnHoldDuration: 500, // Milliseconds to hold head turn before acceptance

      frameWidth: 0, // Store canvas width for percentage calculations
      frameHeight: 0, // Store canvas height

      // iOS Detection
      isIOS: false,
      centerSnapshotBlob: null,
      centerSnapshotTaken: false,

      // FPS Monitoring & Adaptive Performance
      fpsTimestamps: [], // Last second of frame timestamps
      currentFPS: 30, // Estimated current FPS

      // Grace window for gesture cancellation (Android flicker tolerance)
      turnLeftLostSince: null, // Timestamp when left turn first lost
      turnRightLostSince: null, // Timestamp when right turn first lost
      resetGraceMs: 220, // Grace period before cancelling turn (ms)

      // Adaptive thresholds based on FPS
      adaptiveMode: false, // True when running in low-FPS mode
    }
  },

  computed: {
    dataSegmentId() {
      if (this.$route.params.dataSegmentId) {
        return this.$route.params.dataSegmentId
      }
      return this.$store.getters.dataSegmentId
    },
  },

  async mounted() {
    this.$store.dispatch('trackEvent', { event: 'JS | SSA Register Liveness' })
    // Detect iOS devices
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    /**
     * Start liveness flow from intro CTA
     */
    async startLivenessFlow() {
      try {
        this.statusText = 'Starting camera...';
        this.isInitializing = true;
        this.showIntro = false;
        // Initialize MediaPipe first, then camera
        await this.initializeMediaPipe();
        await this.initializeCamera();
        // Finalize ready state like initializeComponent did
        this.statusText = 'Ready to start';
        this.isInitializing = false;
        this.cameraReady = true;
        this.statusClass = 'text-success';
      } catch (error) {
        console.error('Intro start error:', error);
        this.statusText = 'Error: ' + (error && error.message ? error.message : 'Unable to start');
        this.statusClass = 'text-error';
        this.isInitializing = false;
      }
    },

    // Captures the current mirrored frame to a canvas. This part is fast
    // (one rAF tick + a synchronous drawImage), so the pixels are locked in
    // at the correct instant even though encoding them (below) can be slow.
    async _captureMirroredFrame(maxDim = 1280) {
      const v = this.$refs.videoElement;              // you already use ref="videoElement"
      if (!(v && v.readyState >= 2 && v.videoWidth && v.videoHeight)) {
        await new Promise((res, rej) => {
          const to = setTimeout(() => rej(new Error('LIVE_VIDEO_TIMEOUT')), 2500);
          const on = () => { clearTimeout(to); res(); };
          v.addEventListener('canplay', on, { once: true });
        });
      }
      let w = v.videoWidth, h = v.videoHeight, ar = w / h;
      if (w > maxDim || h > maxDim) {
        if (w > h) { w = maxDim; h = Math.round(maxDim / ar); }
        else { h = maxDim; w = Math.round(maxDim * ar); }
      }
      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      const canvas = useOffscreen ? new OffscreenCanvas(w, h)
                                  : Object.assign(document.createElement('canvas'), { width: w, height: h });
      const ctx = canvas.getContext('2d', { alpha: false });
      await new Promise(r => requestAnimationFrame(r));
      ctx.save();                     // begin transform state
      ctx.translate(w, 0);           // move origin to the right edge
      ctx.scale(-1, 1);              // flip horizontally
      ctx.drawImage(v, 0, 0, w, h);  // draw mirrored
      ctx.restore();                 // restore normal state
      return canvas;
    },

    async _encodeCanvasToJpegBlob(canvas, quality = 0.85) {
      if (canvas.convertToBlob) {
        return await canvas.convertToBlob({ type: 'image/jpeg', quality });
      }
      return await new Promise((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob null')), 'image/jpeg', quality);
      });
    },

    async _snapshotFromLiveVideo(maxDim = 1280, quality = 0.85) {
      const canvas = await this._captureMirroredFrame(maxDim);
      return await this._encodeCanvasToJpegBlob(canvas, quality);
    },

    /**
     * Select two random gestures for this liveness check session
     */
    selectRandomGestures() {
      // Create a copy of available gestures and shuffle
      const shuffled = [...this.availableGestures]
        .map(gesture => ({ ...gesture, done: false }))
        .sort(() => Math.random() - 0.5);

      // Select gestures for this session
      this.selectedGestures = shuffled;
      this.currentGestureIndex = 0;

      // Set current gesture
      if (this.selectedGestures.length > 0) {
        this.currentGestureName = this.selectedGestures[0].name;
        this.currentGestureInstruction = this.selectedGestures[0].label;
      }
    },

    /**
     * Move to the next gesture in the sequence
     */
    moveToNextGesture() {
      // Play success sound feedback
      this.playSuccessSound();

      // Mark current gesture as done
      if (this.currentGestureIndex < this.selectedGestures.length) {
        this.selectedGestures[this.currentGestureIndex].done = true;
      }

      this.currentGestureIndex++;

      // Check if all gestures are complete
      if (this.currentGestureIndex >= this.selectedGestures.length) {
        this.allGesturesComplete();
      } else {
        // Set next gesture
        this.currentGestureName = this.selectedGestures[this.currentGestureIndex].name;
        this.currentGestureInstruction = this.selectedGestures[this.currentGestureIndex].label;

        // Reset gesture-specific states
        this.resetGestureState();

        // Reset gesture timer
        this.gestureTimer = 0;

        this.statusText = this.currentGestureInstruction;

        // Play voice instruction for the next gesture
        this.playVoiceInstruction(this.currentGestureName);
      }
    },

    /**
     * Play success sound when gesture is verified
     * Prevents overlapping by resetting playback if already playing
     */
    playSuccessSound() {
      if (!this.successAudio) return;

      try {
        // Reset audio to beginning and play (prevents overlapping)
        this.successAudio.currentTime = 0;

        // Play the sound (returns a promise)
        const playPromise = this.successAudio.play();

        // Handle potential playback issues (especially on mobile browsers)
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.warn('Success audio playback failed:', err);
          });
        }
      } catch (err) {
        console.warn('Error playing success sound:', err);
      }
    },

    /**
     * Load all voice instruction audio files
     * Must be called after user interaction for iOS/Safari compatibility
     */
    loadVoiceInstructions() {
      try {
        this.voiceInstructions = {
          smile: new Audio(require('@/assets/sounds/smile.mp3')),
          turnLeft: new Audio(require('@/assets/sounds/turn-left.mp3')),
          turnRight: new Audio(require('@/assets/sounds/turn-right.mp3')),
          blink: new Audio(require('@/assets/sounds/blink-eyes.mp3')),
          passed: new Audio(require('@/assets/sounds/liveness-passed.mp3')),
          failed: new Audio(require('@/assets/sounds/liveness-failed.mp3'))
        };

        // Preload all audio files
        Object.values(this.voiceInstructions).forEach(audio => {
          audio.preload = 'auto';
          audio.volume = 1;
        });
      } catch (err) {
        console.warn('Failed to load voice instructions:', err);
      }
    },

    /**
     * Play voice instruction for a specific gesture
     * @param {string} gesture - The gesture name (smile, turnLeft, turnRight, blink, passed, failed)
     */
    playVoiceInstruction(gesture) {
      const audio = this.voiceInstructions[gesture];
      if (!audio) return;

      try {
        // Reset audio to beginning and play
        audio.currentTime = 0;

        const playPromise = audio.play();

        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.warn(`Voice instruction playback failed for ${gesture}:`, err);
          });
        }
      } catch (err) {
        console.warn(`Error playing voice instruction for ${gesture}:`, err);
      }
    },

    /**
     * Reset state for current gesture detection
     */
    resetGestureState() {
      // Reset smile detection state (keep baseline and lastSmileTime for session continuity)
      this.smileStartTime = null;
      this.smileFrameCount = 0;
      this.smileRatiosBuffer = [];

      // Reset head turn hold timers
      this.turnLeftStart = null;
      this.turnRightStart = null;
      this.headTurnConfirmationFrames = 0;

      // Don't reset neutral position or baseline - they remain calibrated throughout the session
      // Don't clear headPositionBuffer - keep smoothing active
    },

    /**
     * All gestures completed successfully
     */
    async allGesturesComplete() {
      this.isLivenessPassed = true;
      this.statusText = '✅ Liveness Verified Successfully!';
      this.statusClass = 'text-success';

      // Play success sound first, then liveness passed voice instruction
      this.playSuccessSound();
      setTimeout(() => {
        this.playVoiceInstruction('passed');
      }, 300);

      // Safari + Chrome blob sync fix: Wait for recording to stop and blob to be ready
      const videoBlob = await this.stopRecording();

      // Trigger post-liveness flow
      if (!this.postLivenessHandled) {
        this.postLivenessHandled = true;
        await this.handlePostLivenessFlow(videoBlob);
      }
    },

    /**
     * Handle gesture timeout
     */
    handleGestureTimeout() {
      this.statusText = '⏱️ Gesture not detected. Please try again.';
      this.statusClass = 'text-error';
      this.timedOut = true;
      console.log('Gesture timeout:', this.currentGestureName);

      // Play liveness failed voice instruction
      this.playVoiceInstruction('failed');

      // Stop recording after a short delay to show message
      setTimeout(() => {
        this.stopRecording();
      }, 1000);
    },

    /**
     * Initialize camera and MediaPipe
     */
    async initializeComponent() {
      try {
        this.statusText = 'Initializing camera...';

        // Step 1: Request camera access
        await this.initializeCamera();

        // Step 2: Load MediaPipe
        this.statusText = 'Loading face detection...';
        await this.initializeMediaPipe();

        // Step 3: Ready
        this.statusText = 'Ready to start';
        this.isInitializing = false;
        this.cameraReady = true;
        this.statusClass = 'text-success';
      } catch (error) {
        console.error('Initialization error:', error);
        this.statusText = 'Error: ' + error.message;
        this.statusClass = 'text-error';
        this.isInitializing = false;
      }
    },

    /**
     * Initialize camera using getUserMedia with enhanced quality settings
     */
    async initializeCamera() {
      try {
        const isAndroid = /Android/i.test(navigator.userAgent);
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

        // Use lower ideal resolution only for iOS devices (640x480), keep higher for desktop.
        const constraints = {
          video: {
            facingMode: 'user',
            width: isIOS
              ? { ideal: 640, max: 1280 }
              : { min: 320, ideal: 1280, max: 1920 },
            height: isIOS
              ? { ideal: 480, max: 720 }
              : { min: 240, ideal: 720, max: 1080 },
            frameRate: isIOS
              ? { ideal: 30, max: 30 }
              : (isAndroid ? { ideal: 24, min: 20, max: 30 } : { ideal: 30, max: 60 })
          },
          audio: false
        };

        // Try to add advanced focus constraints (not all browsers support this)
        try {
          constraints.video.advanced = [
            { focusMode: 'continuous' },
            { exposureMode: 'continuous' },
            { whiteBalanceMode: 'continuous' }
          ];
        } catch (err) {
          console.log('Advanced camera constraints not supported');
        }

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Attach stream to video element
        const video = this.$refs.videoElement;
        video.srcObject = this.stream;

        // Safari fix for iPhone recording - prevent fullscreen and ensure inline playback
        if (this.isIOS) {
          video.style.objectFit = 'cover';
          video.setAttribute('playsinline', 'true');
          video.setAttribute('webkit-playsinline', 'true');
        }

        // Wait for video to be ready and stabilized
        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            video.play();
            // Add small delay to let camera auto-focus stabilize
            setTimeout(resolve, 500);
          };
        });

        // Detect supported MIME type for recording
        this.detectSupportedMimeType();
      } catch (error) {
        console.error('Camera initialization error:', error);
        throw new Error('Camera access denied or not available');
      }
    },

    /**
     * Detect the best supported MIME type for this browser
     * Cross-browser MIME handling - Chrome, Safari, Firefox compatibility
     */
    detectSupportedMimeType() {
      // iOS Safari fix: Force MP4 format only
      // Safari falsely reports WebM support but can't play it back for frame capture
      if (this.isIOS) {
        const iosTypes = [
          'video/mp4;codecs=h264',
          'video/mp4;codecs=avc1',
          'video/mp4'
        ];

        for (const type of iosTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            this.supportedMimeType = type;
            console.log('✓ iOS: Selected MIME type:', type);
            return;
          }
        }

        // Fallback for iOS - let browser choose
        this.supportedMimeType = '';
        console.warn('⚠️ iOS: No MP4 MIME type supported, using browser default');
        return;
      }

      // Original logic for non-iOS devices
      const types = [
        // Chrome/Edge preferred formats
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm;codecs=h264',
        'video/webm',
        // Safari/iOS formats
        'video/mp4;codecs=h264',
        'video/mp4;codecs=avc1',
        'video/mp4',
        // Firefox fallback
        'video/x-matroska;codecs=avc1'
      ];

      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          this.supportedMimeType = type;
          console.log('✓ Selected MIME type:', type);
          return;
        }
      }

      // Fallback - let browser choose
      this.supportedMimeType = '';
      console.warn('⚠️ No specific MIME type supported, using browser default');
    },

    /**
     * Load and initialize MediaPipe FaceMesh
     */
    async initializeMediaPipe() {
      try {
        // Avoid re-initializing if already loaded
        if (this.faceMeshLoaded) return;
        // Detect Android for lighter config
        const isAndroid = /Android/i.test(navigator.userAgent);

        // Load MediaPipe scripts dynamically with pinned versions
        await this.loadScriptOnce(
          'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/face_mesh.js',
          'mediapipe-facemesh'
        );

        await this.loadScriptOnce(
          'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils@0.3.1640029074/camera_utils.js',
          'mediapipe-camera-utils'
        );

        // Initialize FaceMesh
        this.faceMesh = new window.FaceMesh({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/${file}`;
          }
        });

        // Android-specific lighter config for better performance
        this.faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: isAndroid ? false : true, // Lighter on Android
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5
        });

        // Set up result callback
        this.faceMesh.onResults(this.onMediaPipeResults);

        // Mark faceMesh as loaded to avoid re-loading
        this.faceMeshLoaded = true;
      } catch (error) {
        console.error('MediaPipe initialization error:', error);
        throw new Error('Failed to load MediaPipe');
      }
    },

    /**
     * Load external script only once
     */
    loadScriptOnce(src, id) {
      return new Promise((resolve, reject) => {
        // Check if already loaded
        if (document.getElementById(id)) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    /**
     * Start recording and liveness detection with pre-start positioning
     */
    async startRecording() {
      try {
        this.isRecording = true;
        this.statusText = 'Position your face in the centre';
        this.statusClass = 'text-recording';
        this.timer = 0;
        this.progress = 0;
        this.gestureTimer = 0;
        this.timedOut = false; // Clear timeout flag

        // Initialize pre-start positioning - wait for proper face position before starting gestures
        this.preStartPositioning = true;
        this.perfectPositionReached = false;
        this.startingLivenessMessage = '';
        this.timerPaused = true; // Pause timer until face is positioned correctly
        this.faceOutOfPosition = false;
        this.dynamicValidationActive = false;

        // Clear any existing face lost timeout
        if (this.faceLostTimeout) {
          clearInterval(this.faceLostTimeout);
          this.faceLostTimeout = null;
        }
        this.faceLostDuration = 0;

        // Select random gestures for this session
        this.selectRandomGestures();

        // Initialize success audio (must be created after user interaction for iOS/Safari)
        if (!this.successAudio) {
          try {
            this.successAudio = new Audio(require('@/assets/sounds/correct.mp3'));
            this.successAudio.volume = 1; // Set moderate volume
            this.successAudio.preload = 'auto'; // Preload for instant playback
          } catch (err) {
            console.warn('Failed to initialize success audio:', err);
          }
        }

        // Initialize voice instruction audio files
        this.loadVoiceInstructions();

        // Reset smile detection states for new session
        this.smileBaseline = null; // Will be recaptured when neutral position is set
        this.smileStartTime = null;
        this.smileFrameCount = 0;
        this.lastSmileTime = 0; // Reset cooldown timer for new session
        this.smileRatiosBuffer = [];

        // Reset head turn detection states
        this.turnLeftStart = null;
        this.turnRightStart = null;
        this.headPositionBuffer = [];
        this.previousNoseX = null;
        this.previousNoseY = null;

        // Reset neutral position calibration
        this.neutralNoseX = null;
        this.neutralNoseY = null;
        this.neutralPositionSet = false;
        this.faceStableFrames = 0;

        // Reset head turn confirmation
        this.headTurnConfirmationFrames = 0;

        this.faceDetected = false;

        // Keep recorded video mirrored - create mirror canvas for recording
        await this.setupMirrorCanvas();

        console.log('Supported MIME type:', this.supportedMimeType);
        // Use detected MIME type or fallback
        const options = this.supportedMimeType
          ? { mimeType: this.supportedMimeType }
          : {};

        // Record from mirrored canvas stream instead of raw camera stream
        this.mediaRecorder = new MediaRecorder(this.mirrorStream, options);
        this.recordedChunks = [];

        console.log('MediaRecorder started');
        // Collect video chunks during recording
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        // Set onstop handler to finalize blob when recording stops
        this.mediaRecorder.onstop = () => {
          const blobType = this.supportedMimeType || 'video/mp4';
          this.videoBlob = new Blob(this.recordedChunks, { type: blobType });

          const sizeMB = this.videoBlob.size / (1024 * 1024);

          if (sizeMB > 25) {
            console.warn('Video file is large (>25MB). Consider reducing quality.');
          }
        };

        // Start recording
        this.mediaRecorder.start();

        // Start timer
        this.startTimer();
        // Start face detection loop
        this.startFaceDetection();
        // Start mirror canvas rendering loop
        this.renderMirrorCanvas();
      } catch (error) {
        console.error('Recording error:', error);
        this.statusText = 'Error starting recording';
        this.statusClass = 'text-error';
        this.isRecording = false;
      }
    },

    /**
     * Setup mirror canvas for recording
     * Keep recorded video mirrored - this ensures the final video matches the preview
     */
    async setupMirrorCanvas() {
      const video = this.$refs.videoElement;

      // Create offscreen canvas for mirrored recording
      this.mirrorCanvas = document.createElement('canvas');
      this.mirrorCanvas.width = video.videoWidth || 1280;
      this.mirrorCanvas.height = video.videoHeight || 720;
      this.mirrorCanvasContext = this.mirrorCanvas.getContext('2d');

      // Get stream from mirrored canvas
      this.mirrorStream = this.mirrorCanvas.captureStream(30); // 30 FPS
    },

    /**
     * Render mirrored video to canvas continuously
     * Keep recorded video mirrored - applies horizontal flip to match live preview
     */
    renderMirrorCanvas() {
      const render = () => {
        if (!this.isRecording) return;

        const video = this.$refs.videoElement;
        const ctx = this.mirrorCanvasContext;

        if (ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
          // Disable image smoothing for sharper video quality
          ctx.imageSmoothingEnabled = false;
          ctx.imageSmoothingQuality = 'high';

          // Clear canvas
          ctx.clearRect(0, 0, this.mirrorCanvas.width, this.mirrorCanvas.height);

          // Apply horizontal mirroring
          ctx.save();
          ctx.translate(this.mirrorCanvas.width, 0);
          ctx.scale(-1, 1);

          // Draw mirrored video frame with crisp rendering
          ctx.drawImage(video, 0, 0, this.mirrorCanvas.width, this.mirrorCanvas.height);

          ctx.restore();
        }

        // Continue rendering loop
        if (this.isRecording) {
          requestAnimationFrame(render);
        }
      };

      render();
    },

    /**
     * Start timer and progress tracking (with pause/resume support and position tracking)
     */
    startTimer() {
      this.timerInterval = setInterval(() => {
        // Skip timer increment if paused (during pre-start positioning or face out of position)
        if (this.timerPaused) {
          return;
        }

        this.timer++;

        // Track total recording time and valid position time for final validation
        if (this.dynamicValidationActive) {
          this.totalRecordingTime++;

          // Check if face is in valid position (centered + correct distance)
          const isInValidPosition = this.faceCentered && this.faceDistance === 'perfect' && !this.faceOutOfPosition;
          if (isInValidPosition) {
            this.validPositionTime++;
          }
        }

        // Track gesture-specific timer when gestures are active
        if (this.selectedGestures.length > 0 && this.faceDetected && !this.preStartPositioning) {
          this.gestureTimer++;

          // Check if gesture timeout reached
          if (this.gestureTimer >= this.gestureTimeout) {
            this.handleGestureTimeout();
          }
        }

        // Overall progress based on max recording time
        this.progress = (this.timer / this.maxRecordingTime) * 100;

        // Check if max total time reached
        if (this.timer >= this.maxRecordingTime) {
          this.checkLivenessResult();
        }
      }, 1000);
    },

    /**
     * Pause timer when face is out of position
     */
    pauseTimer() {
      this.timerPaused = true;
      console.log('⏸️ Timer paused - face out of position');
    },

    /**
     * Resume timer when face returns to correct position
     */
    resumeTimer() {
      this.timerPaused = false;
      console.log('▶️ Timer resumed - face back in position');
    },

    /**
     * Start actual liveness session after perfect positioning is reached
     */
    startLivenessSession() {
      // Transition from pre-start positioning to actual liveness
      this.preStartPositioning = false;
      this.perfectPositionReached = false;
      this.dynamicValidationActive = true;

      // Resume timer now that positioning is correct
      this.timerPaused = false;

      // Show brief success message
      this.startingLivenessMessage = 'Perfect! Starting liveness check...';
      this.statusText = this.startingLivenessMessage;

      // Clear success message after 1 second and continue with gestures
      setTimeout(() => {
        this.startingLivenessMessage = '';
        this.statusText = this.currentGestureInstruction || 'Follow the gestures';
      }, 1000);
    },

    /**
     * Start face detection loop using MediaPipe
     * Uses requestVideoFrameCallback when available for better frame sync
     */
    async startFaceDetection() {
      const video = this.$refs.videoElement;

      const detectFace = async () => {
        if (!this.isRecording) return;

        try {
          // Throttle processing to reduce CPU/GPU load, but keep responsive for gestures.
          // Target ~12-15 FPS => ~66-83 ms interval. Use 70ms default.
          const now = Date.now();
          const throttleMs = 70;

          // Consider gesture-active periods: when a gesture is currently being timed/checked,
          // bypass throttling to avoid skipping important frames. Use existing gesture state
          // instead of adding new flags.
          const inActiveGesturePeriod = !!(
            this.currentGestureName ||
            this.gestureTimer > 0 ||
            this.dynamicValidationActive ||
            this.smileStartTime ||
            this.turnLeftStart ||
            this.turnRightStart
          );

          if (!inActiveGesturePeriod && (now - this.lastDetectionTime < throttleMs)) {
            // Schedule next frame and exit early
            if (video.requestVideoFrameCallback) {
              this.videoFrameCallbackId = video.requestVideoFrameCallback(detectFace);
            } else {
              this.rafId = requestAnimationFrame(detectFace);
            }
            return;
          }

          this.lastDetectionTime = now;
          await this.faceMesh.send({ image: video });
        } catch (error) {
          // ignore per-frame detection errors
        }

        // Continue loop using video frame callback when available
        if (this.isRecording) {
          if (video.requestVideoFrameCallback) {
            this.videoFrameCallbackId = video.requestVideoFrameCallback(detectFace);
          } else {
            this.rafId = requestAnimationFrame(detectFace);
          }
        }
      };

      detectFace();
    },

    /**
     * MediaPipe results callback
     */
    onMediaPipeResults(results) {
      if (!this.isRecording) return;

      const canvas = this.$refs.canvasElement;
      const ctx = canvas.getContext('2d');
      const video = this.$refs.videoElement;

      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Store frame dimensions for head movement calculations
      this.frameWidth = canvas.width;
      this.frameHeight = canvas.height;

      // Disable image smoothing for crisp rendering
      ctx.imageSmoothingEnabled = false;

      // Draw video frame
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
        this.faceDetected = true;
        const landmarks = results.multiFaceLandmarks[0];

        // Check if face is properly centered
        this.checkFaceCentering(landmarks, canvas.width, canvas.height);

        // Check face distance for mobile guidance
        this.checkFaceDistance(landmarks);

        // Draw face mesh
        this.drawFaceMesh(ctx, landmarks, canvas.width, canvas.height);

        // Draw centering guide if needed
        if (this.showCenteringGuide && !this.neutralPositionSet) {
          this.drawCenteringGuide(ctx, canvas.width, canvas.height);
        }

        // Perform liveness checks with frame dimensions
        this.performLivenessChecks(landmarks, canvas.width, canvas.height);
      } else {
        this.faceDetected = false;
        this.faceCentered = false;
      }
    },

    /**
     * Check if face is properly centered in the frame (with pre-start and dynamic validation)
     */
    checkFaceCentering(landmarks, canvasWidth, canvasHeight) {
      if (!landmarks || landmarks.length === 0) {
        this.faceCentered = false;
        this.facePositionMessage = '';

        // Handle face lost during dynamic validation
        if (this.dynamicValidationActive && !this.faceOutOfPosition) {
          this.handleFaceLost();
        }
        return;
      }

      // Calculate face bounding box from landmarks
      let xMin = Infinity, xMax = -Infinity;
      let yMin = Infinity, yMax = -Infinity;

      landmarks.forEach(landmark => {
        xMin = Math.min(xMin, landmark.x);
        xMax = Math.max(xMax, landmark.x);
        yMin = Math.min(yMin, landmark.y);
        yMax = Math.max(yMax, landmark.y);
      });

      // Calculate face center and frame center (normalized 0-1)
      const faceCenterX = (xMin + xMax) / 2;
      const faceCenterY = (yMin + yMax) / 2;
      const frameCenterX = 0.5;
      const frameCenterY = 0.5;

      // Calculate offsets from center
      const horizontalOffset = Math.abs(faceCenterX - frameCenterX);
      const verticalOffset = Math.abs(faceCenterY - frameCenterY);

      // Check if face is within tolerance zones (±15% of frame)
      const horizontalTolerance = this.faceCenteringTolerance;
      const verticalTolerance = this.faceCenteringTolerance;

      let message = '';
      this.faceCentered = true;

      // Check horizontal centering first (most important for gestures)
      if (horizontalOffset > horizontalTolerance) {
        message = faceCenterX < frameCenterX ? 'Move face slightly right' : 'Move face slightly left';
        this.faceCentered = false;
      }
      // Check vertical centering if horizontal is OK
      else if (verticalOffset > verticalTolerance) {
        message = faceCenterY < frameCenterY ? 'Move face slightly down' : 'Move face slightly up';
        this.faceCentered = false;
      }
      // Face is centered
      else {
        message = 'Face centered ✓';
        this.faceCentered = true;
      }

      this.facePositionMessage = message;

      // Update real-time position validation flag (used for gesture validation)
      // This ensures gestures only count when face is properly positioned
      this.updateFacePositionValidation();

      // Handle pre-start positioning logic
      if (this.preStartPositioning) {
        this.handlePreStartPositioning();
      }
      // Handle dynamic validation during liveness session
      else if (this.dynamicValidationActive) {
        this.handleDynamicValidation();
      }
      // Update status during calibration phase only
      else if (!this.neutralPositionSet && this.faceDetected) {
        this.statusText = message;
      }
    },

    /**
     * Update real-time face position validation flag
     * Used to ensure gestures only count when face is properly positioned
     */
    updateFacePositionValidation() {
      // Face is in valid position only when:
      // 1. Face is centered within tolerance zones
      // 2. Distance is perfect (not too close or too far)
      // 3. Face is detected
      const isCentered = this.faceCentered;
      const isGoodDistance = this.faceDistance === 'perfect';
      const isDetected = this.faceDetected;

      this.isFaceInValidPosition = isCentered && isGoodDistance && isDetected;
    },

    /**
     * Show brief warning when gesture is ignored due to bad positioning
     */
    showGestureIgnoredWarning() {
      this.gestureIgnoredWarning = 'Please center your face before continuing';

      // Clear any existing timeout
      if (this.gestureIgnoredTimeout) {
        clearTimeout(this.gestureIgnoredTimeout);
      }

      // Auto-hide warning after 1.5 seconds
      this.gestureIgnoredTimeout = setTimeout(() => {
        this.gestureIgnoredWarning = '';
        this.gestureIgnoredTimeout = null;
      }, 1500);
    },

    /**
     * Handle pre-start positioning logic - check if both centering and distance are perfect
     */
    handlePreStartPositioning() {
      const isCentered = this.faceCentered;
      const isGoodDistance = this.faceDistance === 'perfect';

      if (isCentered && isGoodDistance && !this.perfectPositionReached) {
        // Both centering and distance are perfect - start liveness session
        this.perfectPositionReached = true;
        this.startLivenessSession();
      } else if (!isCentered || !isGoodDistance) {
        // Show positioning guidance
        let guidanceMessage = '';
        if (!isCentered && !isGoodDistance) {
          guidanceMessage = this.facePositionMessage + ' • ' + this.faceDistanceMessage;
        } else if (!isCentered) {
          guidanceMessage = this.facePositionMessage;
        } else if (!isGoodDistance) {
          guidanceMessage = this.faceDistanceMessage;
        }
        this.statusText = guidanceMessage;
      }
    },

    /**
     * Handle dynamic validation during liveness session - pause/resume timer based on face position
     */
    handleDynamicValidation() {
      const isCentered = this.faceCentered;
      const isGoodDistance = this.faceDistance === 'perfect';
      const isInPosition = isCentered && isGoodDistance;

      if (!isInPosition && !this.faceOutOfPosition) {
        // Face moved out of position - pause timer and show guidance
        this.faceOutOfPosition = true;
        this.pauseTimer();
        this.statusText = 'Face out of position — please return to center';

        // Start face lost timeout
        this.startFaceLostTimeout();
      } else if (isInPosition && this.faceOutOfPosition) {
        // Face returned to position - resume timer
        this.faceOutOfPosition = false;
        this.resumeTimer();
        this.clearFaceLostTimeout();
        this.statusText = this.currentGestureInstruction || 'Follow the gestures';
      }
    },

    /**
     * Handle face lost detection - pause timer and start timeout
     */
    handleFaceLost() {
      if (!this.faceOutOfPosition) {
        this.faceOutOfPosition = true;
        this.pauseTimer();
        this.statusText = 'Face lost — please return to center';
        this.startFaceLostTimeout();
      }
    },

    /**
     * Start timeout for face lost detection (5 seconds)
     */
    startFaceLostTimeout() {
      this.clearFaceLostTimeout();
      this.faceLostDuration = 0;

      this.faceLostTimeout = setInterval(() => {
        this.faceLostDuration++;

        if (this.faceLostDuration >= this.maxFaceLostDuration) {
          this.handleFaceLostTimeout();
        }
      }, 1000);
    },

    /**
     * Clear face lost timeout
     */
    clearFaceLostTimeout() {
      if (this.faceLostTimeout) {
        clearInterval(this.faceLostTimeout);
        this.faceLostTimeout = null;
        this.faceLostDuration = 0;
      }
    },

    /**
     * Handle face lost timeout - stop session and show retry
     */
    handleFaceLostTimeout() {
      console.log('❌ Face lost timeout reached - stopping session');
      this.clearFaceLostTimeout();
      this.stopRecording();
      this.statusText = 'Face lost — please retry';
      this.statusClass = 'text-error';
      this.timedOut = true;

      // Mark liveness as completed but failed
      this.livenessCompleted = true;
      this.livenessSuccess = false;
    },

    /**
     * Check face distance and provide feedback (Mobile-friendly)
     * Uses face size relative to frame to determine if user is too close/far
     */
    checkFaceDistance(landmarks) {
      if (!landmarks || landmarks.length === 0) {
        this.faceDistance = 'unknown';
        this.faceDistanceMessage = '';
        return;
      }

      // Calculate face bounding box
      let xMin = Infinity, xMax = -Infinity;
      let yMin = Infinity, yMax = -Infinity;

      landmarks.forEach(landmark => {
        xMin = Math.min(xMin, landmark.x);
        xMax = Math.max(xMax, landmark.x);
        yMin = Math.min(yMin, landmark.y);
        yMax = Math.max(yMax, landmark.y);
      });

      // Calculate face size (normalized 0-1)
      const faceWidth = xMax - xMin;
      const faceHeight = yMax - yMin;
      const faceSizeRatio = Math.max(faceWidth, faceHeight);

      // Distance thresholds based on face size
      // Perfect: 45-65% of frame
      // Too far: < 40%
      // Too close: > 70%
      if (faceSizeRatio < 0.40) {
        this.faceDistance = 'too-far';
        this.faceDistanceMessage = '📱 Move closer';
      } else if (faceSizeRatio > 0.70) {
        this.faceDistance = 'too-close';
        this.faceDistanceMessage = '📱 Move back';
      } else {
        this.faceDistance = 'perfect';
        this.faceDistanceMessage = '✓ Perfect distance';
      }
    },

    /**
     * Draw visual centering guide overlay
     */
    drawCenteringGuide(ctx, width, height) {
      const centerX = width / 2;
      const centerY = height / 2;
      const ovalWidth = width * 0.35;
      const ovalHeight = height * 0.5;

      // Draw semi-transparent overlay with center cutout
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(0, 0, width, height);

      // Clear center oval area
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, ovalWidth, ovalHeight, 0, 0, 2 * Math.PI);
      ctx.fill();

      // Draw guide outline
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = this.faceCentered ? 'rgba(16, 185, 129, 0.8)' : 'rgba(251, 191, 36, 0.8)';
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 5]);
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, ovalWidth, ovalHeight, 0, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw guide text
      if (!this.faceCentered && this.facePositionMessage) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(this.facePositionMessage, centerX, height - 40);
      } else if (this.faceCentered) {
        ctx.fillStyle = 'rgba(16, 185, 129, 0.9)';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Perfect! Hold still...', centerX, height - 40);
      }
    },

    /**
     * Draw face mesh overlay (mirrored to match video display)
     */
    drawFaceMesh(ctx, landmarks, width, height) {
      // Note: The canvas is already mirrored via CSS transform: scaleX(-1)
      // So we draw normally and the CSS handles the mirroring

      ctx.fillStyle = 'rgba(0, 255, 0, 0.5)';

      landmarks.forEach((landmark) => {
        const x = landmark.x * width;
        const y = landmark.y * height;
        ctx.beginPath();
        ctx.arc(x, y, 1, 0, 2 * Math.PI);
        ctx.fill();
      });

      // Draw face outline
      ctx.strokeStyle = 'rgba(0, 255, 0, 0.8)';
      ctx.lineWidth = 2;
      ctx.beginPath();

      // Simple face contour (indices for face oval)
      const faceOval = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109];

      faceOval.forEach((index, i) => {
        const landmark = landmarks[index];
        const x = landmark.x * width;
        const y = landmark.y * height;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.closePath();
      ctx.stroke();
    },

    /**
     * Perform gesture-based liveness checks
     */
    performLivenessChecks(landmarks, canvasWidth, canvasHeight) {
      // ========== FPS MONITORING & ADAPTIVE PERFORMANCE ==========
      const now = performance.now();
      this.fpsTimestamps.push(now);

      // Keep only timestamps from last 1 second
      this.fpsTimestamps = this.fpsTimestamps.filter(t => now - t < 1000);

      // Calculate current FPS
      this.currentFPS = this.fpsTimestamps.length;

      // Detect Android for adaptive mode
      const isAndroid = /Android/i.test(navigator.userAgent);

      // Adapt thresholds when FPS is low OR on Android
      if (this.currentFPS < 24 || isAndroid) {
        if (!this.adaptiveMode) {
          this.adaptiveMode = true;
        }
        // Lower hold duration for faster gesture acceptance on low-end devices
        this.headTurnHoldDuration = 500;
        this.resetGraceMs = 220;
      } else if (this.currentFPS >= 26) {
        if (this.adaptiveMode) {
          this.adaptiveMode = false;
        }
        // Restore original thresholds for high-FPS devices
        this.headTurnHoldDuration = 500; // Keep original value
        this.resetGraceMs = 220;
      }

      // Use nose tip (landmark 1) as the reference point for head tracking
      const noseTip = landmarks[1];

      // Work with normalized coordinates (0-1) for consistent detection across resolutions
      const currentNoseX = noseTip.x;
      const currentNoseY = noseTip.y;

      // Add current position to rolling buffer for smoothing
      this.headPositionBuffer.push({ x: currentNoseX, y: currentNoseY });

      // Keep buffer size limited
      if (this.headPositionBuffer.length > this.bufferSize) {
        this.headPositionBuffer.shift(); // Remove oldest position
      }

      // Calculate smoothed position using rolling average
      const smoothedPosition = this.calculateSmoothedPosition();

      // ========== CALIBRATE NEUTRAL POSITION ==========
      // Wait for face to be stable AND centered before setting neutral position
      if (!this.neutralPositionSet) {
        // Only calibrate if face is properly centered
        if (!this.faceCentered) {
          // Show centering message (already set in checkFaceCentering)
          this.faceStableFrames = 0; // Reset stability counter
        } else {
          // Show calibration status when centered
          this.statusText = `Calibrating... Hold still (${Math.round((this.faceStableFrames / this.faceStableThreshold) * 100)}%)`;

          // Check if position is stable by comparing with previous position
          if (this.previousNoseX !== null) {
            const positionDrift = Math.abs(smoothedPosition.x - this.previousNoseX);

            // If position is stable (drift < 0.01), increment counter
            if (positionDrift < 0.01) {
              this.faceStableFrames++;

              // Once stable for enough frames, set neutral position
              if (this.faceStableFrames >= this.faceStableThreshold) {
                this.neutralNoseX = smoothedPosition.x;
                this.neutralNoseY = smoothedPosition.y;
                this.neutralPositionSet = true;

                this.showCenteringGuide = false; // Hide guide after calibration

                // Capture smile baseline when neutral position is established
                this.captureSmileBaseline(landmarks);

                // Announce the first gesture only once the still FRAME is captured
                // (fast — one rAF tick + a synchronous draw), not once it's fully
                // JPEG-encoded (slow on some Android devices). This keeps the photo
                // locked to the "looking straight at camera" instant without making
                // the user wait through the encode before they see/hear what to do.
                const announceFirstGesture = () => {
                  if (this.selectedGestures.length > 0) {
                    this.statusText = this.currentGestureInstruction;
                    this.playVoiceInstruction(this.currentGestureName);
                  }
                };

                // Capture a still frame of the centred face - this becomes the uploaded profile picture
                if (!this.centerSnapshotTaken) {
                  try {
                    // brief hold (~200ms), then take the snapshot
                    setTimeout(() => {
                      this._captureMirroredFrame()
                        .then((canvas) => {
                          // Pixels are locked in now - safe to move on to the next
                          // gesture without waiting for the encode below.
                          this.centerSnapshotTaken = true;
                          announceFirstGesture();
                          return this._encodeCanvasToJpegBlob(canvas);
                        })
                        .then((blob) => {
                          this.centerSnapshotBlob = blob;
                        })
                        .catch((e) => {
                          console.error('Center snapshot failed:', e);
                          // don't block the flow; proceed without a center snapshot
                          if (!this.centerSnapshotTaken) {
                            this.centerSnapshotTaken = true;
                            announceFirstGesture();
                          }
                        });
                    }, 50);
                  } catch (e) {
                    console.error('Center snapshot scheduling failed:', e);
                    this.centerSnapshotTaken = true;
                    announceFirstGesture();
                  }
                } else {
                  announceFirstGesture();
                }
              }
            } else {
              // Reset counter if face moved
              this.faceStableFrames = 0;
            }
          }
        }
      }

      // Update previous position
      this.previousNoseX = smoothedPosition.x;
      this.previousNoseY = smoothedPosition.y;

      // ========== GESTURE DETECTION (only after neutral is set) ==========
      if (this.neutralPositionSet) {
        if (this.currentGestureName === 'smile') {
          this.detectSmileGesture(landmarks);
        } else if (this.currentGestureName === 'turnLeft') {
          this.detectHeadTurnLeft(smoothedPosition);
        } else if (this.currentGestureName === 'turnRight') {
          this.detectHeadTurnRight(smoothedPosition);
        }
      }
    },

    /**
     * Capture smile baseline (neutral mouth ratio) when face is stable
     * This is called once per session when neutral position is established
     */
    captureSmileBaseline(landmarks) {
      if (!landmarks || landmarks.length === 0) return;

      const leftMouth = landmarks[61];   // Left corner of mouth
      const rightMouth = landmarks[291]; // Right corner of mouth
      const leftEye = landmarks[33];     // Left eye (inner corner)
      const rightEye = landmarks[263];   // Right eye (inner corner)

      const mouthWidth = Math.abs(rightMouth.x - leftMouth.x);
      const eyeDistance = Math.abs(rightEye.x - leftEye.x);

      if (eyeDistance > 0) {
        this.smileBaseline = mouthWidth / eyeDistance;
      }
    },

    /**
     * Calculate variance of values in array (for stability check)
     */
    calculateVariance(values) {
      if (values.length === 0) return 0;
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
      return variance;
    },

    /**
     * Detect smile gesture using baseline + delta approach
     * Requires BOTH hold time AND consecutive frames, plus stability check
     * Only accepts smile when face is in valid position
     */
    detectSmileGesture(landmarks) {
      // Only run detection if face is in valid position
      if (!this.isFaceInValidPosition) {
        // Reset all smile state if face moves out of position
        if (this.smileStartTime !== null || this.smileFrameCount > 0) {
          this.showGestureIgnoredWarning();
        }
        this.smileStartTime = null;
        this.smileFrameCount = 0;
        this.smileRatiosBuffer = [];
        return;
      }

      // Wait for baseline to be established
      if (this.smileBaseline === null) {
        return;
      }

      // Calculate current smile ratio (normalized)
      const leftMouth = landmarks[61];   // Left corner of mouth
      const rightMouth = landmarks[291]; // Right corner of mouth
      const leftEye = landmarks[33];     // Left eye (inner corner)
      const rightEye = landmarks[263];   // Right eye (inner corner)

      const mouthWidth = Math.abs(rightMouth.x - leftMouth.x);
      const eyeDistance = Math.abs(rightEye.x - leftEye.x);
      const smileRatio = mouthWidth / eyeDistance;

      const now = Date.now();

      // Check if ratio is above baseline + delta threshold
      const isAboveThreshold = smileRatio > (this.smileBaseline + this.smileThresholdDelta);

      if (isAboveThreshold) {
        // Increment frame counter
        this.smileFrameCount++;

        // Start timer on first detection
        if (!this.smileStartTime) {
          this.smileStartTime = now;
        }

        // Add to stability buffer
        this.smileRatiosBuffer.push(smileRatio);
        if (this.smileRatiosBuffer.length > this.SMILE_BUFFER_LEN) {
          this.smileRatiosBuffer.shift(); // Keep buffer size limited
        }

        // Check if ALL conditions are met:
        // 1. Enough consecutive frames
        // 2. Hold time satisfied
        // 3. Cooldown period has passed
        // 4. Stability check (low variance in ratio)
        const framesCondition = this.smileFrameCount >= this.SMILE_FRAMES_COUNT;
        const timeCondition = (now - this.smileStartTime) >= this.SMILE_HOLD_MS;
        const cooldownCondition = (now - this.lastSmileTime) >= this.SMILE_COOLDOWN_MS;

        // Stability check: variance should be low (smile held consistently)
        const variance = this.calculateVariance(this.smileRatiosBuffer);
        const stabilityCondition = variance < 0.004; // Low variance = stable smile

        if (framesCondition && timeCondition && cooldownCondition && stabilityCondition) {
          this.lastSmileTime = now; // Record time of successful detection
          this.smileStartTime = null; // Reset timer
          this.smileFrameCount = 0; // Reset frame counter
          this.smileRatiosBuffer = []; // Clear buffer
          this.moveToNextGesture();
        }
      } else {
        // Reset all state if ratio drops below threshold
        if (this.smileStartTime !== null || this.smileFrameCount > 0) {
          console.log('😐 Smile lost, resetting (frames:', this.smileFrameCount, ')');
        }
        this.smileStartTime = null;
        this.smileFrameCount = 0;
        this.smileRatiosBuffer = [];
      }
    },

    /**
     * Detect head turn left gesture with hold delay and grace window
     * User looks left - In mirrored view, appears on left side
     * Note: Camera is mirrored, so we invert the detection logic
     * Grace window prevents Android flicker from cancelling too early
     */
    detectHeadTurnLeft(smoothedPosition) {
      if (this.neutralNoseX === null) return;

      // Calculate deviation from neutral position (normalized coordinates)
      const deviation = smoothedPosition.x - this.neutralNoseX;
      const now = Date.now();

      // INVERTED: In mirrored camera, looking left = positive deviation (nose moves right in raw coordinates)
      // Head turn detection with hold delay
      if (deviation > this.headTurnThresholdNormalized && this.isFaceInValidPosition) {
        // Clear any "lost" timer since turn is valid again
        this.turnLeftLostSince = null;

        // Start timer when turn first detected
        if (!this.turnLeftStart) {
          this.turnLeftStart = now;
        }

        // Check if turn has been held long enough
        const turnHeldDuration = now - this.turnLeftStart;
        if (turnHeldDuration >= this.headTurnHoldDuration) {
          this.turnLeftStart = null; // Reset timer
          this.turnLeftLostSince = null;
          this.moveToNextGesture();
        }
      } else {
        // Grace window: don't cancel immediately, allow brief deviation
        if (this.turnLeftStart !== null) {
          if (this.turnLeftLostSince === null) {
            // First frame where turn is lost - start grace timer
            this.turnLeftLostSince = now;
          } else {
            // Check if turn has been lost beyond grace period
            const lostDuration = now - this.turnLeftLostSince;
            if (lostDuration >= this.resetGraceMs) {
              // Cancel turn only after grace period
              console.log('Head turn left reset - returned to center or face out of position');
              if (!this.isFaceInValidPosition) {
                this.showGestureIgnoredWarning();
              }
              this.turnLeftStart = null;
              this.turnLeftLostSince = null;
            }
          }
        } else {
          // No turn in progress, reset grace timer
          this.turnLeftLostSince = null;
        }
      }
    },

    /**
     * Detect head turn right gesture with hold delay and grace window
     * User looks right - In mirrored view, appears on right side
     * Note: Camera is mirrored, so we invert the detection logic
     * Grace window prevents Android flicker from cancelling too early
     */
    detectHeadTurnRight(smoothedPosition) {
      if (this.neutralNoseX === null) return;

      // Calculate deviation from neutral position (normalized coordinates)
      const deviation = smoothedPosition.x - this.neutralNoseX;
      const now = Date.now();

      // INVERTED: In mirrored camera, looking right = negative deviation (nose moves left in raw coordinates)
      // Head turn detection with hold delay
      if (deviation < -this.headTurnThresholdNormalized && this.isFaceInValidPosition) {
        // Clear any "lost" timer since turn is valid again
        this.turnRightLostSince = null;

        // Start timer when turn first detected
        if (!this.turnRightStart) {
          this.turnRightStart = now;
        }

        // Check if turn has been held long enough
        const turnHeldDuration = now - this.turnRightStart;
        if (turnHeldDuration >= this.headTurnHoldDuration) {
          this.turnRightStart = null; // Reset timer
          this.turnRightLostSince = null;
          this.moveToNextGesture();
        }
      } else {
        // Grace window: don't cancel immediately, allow brief deviation
        if (this.turnRightStart !== null) {
          if (this.turnRightLostSince === null) {
            // First frame where turn is lost - start grace timer
            this.turnRightLostSince = now;
          } else {
            // Check if turn has been lost beyond grace period
            const lostDuration = now - this.turnRightLostSince;
            if (lostDuration >= this.resetGraceMs) {
              // Cancel turn only after grace period
              console.log('Head turn right reset - returned to center or face out of position');
              if (!this.isFaceInValidPosition) {
                this.showGestureIgnoredWarning();
              }
              this.turnRightStart = null;
              this.turnRightLostSince = null;
            }
          }
        } else {
          // No turn in progress, reset grace timer
          this.turnRightLostSince = null;
        }
      }
    },

    /**
     * Calculate smoothed position using rolling average
     * This reduces noise and prevents false positives from camera shake
     */
    calculateSmoothedPosition() {
      if (this.headPositionBuffer.length === 0) {
        return { x: 0, y: 0 };
      }

      const sum = this.headPositionBuffer.reduce(
        (acc, pos) => ({
          x: acc.x + pos.x,
          y: acc.y + pos.y
        }),
        { x: 0, y: 0 }
      );

      return {
        x: sum.x / this.headPositionBuffer.length,
        y: sum.y / this.headPositionBuffer.length
      };
    },

    /**
     * Check liveness result when timer ends (with final validation)
     */
    checkLivenessResult() {
      // Perform final validation: check if face was in valid position for at least 80% of time
      if (this.dynamicValidationActive && this.totalRecordingTime > 0) {
        const validPositionRatio = this.validPositionTime / this.totalRecordingTime;

        if (validPositionRatio < this.minValidPositionRatio) {
          // Face was not properly centered for enough time - fail validation
          this.livenessValidationFailed = true;
          this.statusText = 'Liveness check failed — face was not properly centered';
          this.statusClass = 'text-error';

          // Play liveness failed voice instruction
          this.playVoiceInstruction('failed');

          this.stopRecording();

          // Mark liveness as completed but failed
          this.livenessCompleted = true;
          this.livenessSuccess = false;
          return;
        }
      }

      // Original liveness check logic
      if (!this.isLivenessPassed) {
        this.statusText = '❌ Verification failed - insufficient liveness detected';
        this.statusClass = 'text-error';
        this.timedOut = true; // Mark as timed out for UI display

        // Play liveness failed voice instruction
        this.playVoiceInstruction('failed');

        // Mark liveness as completed but failed
        this.livenessCompleted = true;
        this.livenessSuccess = false;
      }
      this.stopRecording();
    },

    /**
     * Stop recording and return blob when ready
     * Safari + Chrome blob sync fix - ensures blob is available before proceeding
     */
    async stopRecording() {
      this.isRecording = false;

      // Clear timer
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      // Return a Promise that resolves when the video blob is ready
      return new Promise((resolve) => {
        if (!this.mediaRecorder) {
          return resolve(null);
        }

        // If already inactive, return existing blob
        if (this.mediaRecorder.state === 'inactive') {
          return resolve(this.videoBlob);
        }

        // Store the original onstop handler
        const originalOnstop = this.mediaRecorder.onstop;

        // Wrap the existing onstop handler to wait for blob creation
        this.mediaRecorder.onstop = () => {
          // Call the original onstop to create the blob
          if (originalOnstop) {
            originalOnstop();
          }

          // Wait a bit for blob finalization (especially for Safari)
          setTimeout(() => {
            if (!this.videoBlob || this.videoBlob.size === 0) {
              resolve(null);
            } else {
              resolve(this.videoBlob);
            }
          }, 100); // Small delay to ensure blob is fully created
        };

        this.mediaRecorder.stop();
      });
    },

    /**
     * Preview recorded video
     */
    async previewRecordedVideo() {
      const video = this.$refs.videoElement;

      // Safari fix for iPhone recording - add small delay before playback
      if (this.isIOS) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Show recorded video
      video.srcObject = null;

      // Create object URL for blob
      const videoURL = URL.createObjectURL(this.videoBlob);
      video.src = videoURL;
      video.loop = true;

      // Safari fix for iPhone recording - ensure video is ready before playing
      video.onloadedmetadata = () => {
        video.play().catch(err => {
          console.log('Video playback error:', err);
        });
      };
    },

    /**
     * Retry liveness check
     */
    async retry() {
      // Reset states
      this.videoBlob = null;
      this.isLivenessPassed = false;
      this.statusText = 'Ready to start';
      this.statusClass = 'text-success';
      this.timer = 0;
      this.progress = 0;
      this.gestureTimer = 0;
      this.timedOut = false; // Clear timeout flag

      // Reset post-liveness handling
      this.postLivenessHandled = false;
      this.showRetry = false;

      // Reset gesture selection
      this.selectedGestures = [];
      this.currentGestureIndex = 0;
      this.currentGestureName = '';
      this.currentGestureInstruction = '';

      // Reset smile detection states for retry
      this.smileBaseline = null; // Will be recaptured when neutral position is set
      this.smileStartTime = null;
      this.smileFrameCount = 0;
      this.lastSmileTime = 0; // Reset cooldown timer for retry
      this.smileRatiosBuffer = [];

      // Reset head movement detection
      this.previousNoseX = null;
      this.previousNoseY = null;
      this.headPositionBuffer = [];
      this.turnLeftStart = null;
      this.turnRightStart = null;

      // Reset neutral position calibration
      this.neutralNoseX = null;
      this.neutralNoseY = null;
      this.neutralPositionSet = false;
      this.faceStableFrames = 0;

      // Reset head turn confirmation
      this.headTurnConfirmationFrames = 0;

      // Reset face centering
      this.faceCentered = true;
      this.facePositionMessage = '';
      this.showCenteringGuide = true;

      // Reset pre-start positioning and dynamic validation
      this.preStartPositioning = false;
      this.perfectPositionReached = false;
      this.startingLivenessMessage = '';
      this.timerPaused = false;
      this.faceOutOfPosition = false;
      this.dynamicValidationActive = false;

      // Reset final validation
      this.validPositionTime = 0;
      this.totalRecordingTime = 0;
      this.livenessValidationFailed = false;

      // Clear face lost timeout
      this.clearFaceLostTimeout();
      this.faceLostDuration = 0;

      // Clear gesture ignored warning timeout
      if (this.gestureIgnoredTimeout) {
        clearTimeout(this.gestureIgnoredTimeout);
        this.gestureIgnoredTimeout = null;
      }
      this.gestureIgnoredWarning = '';
      this.isFaceInValidPosition = false;

      // Reset post-liveness UI state
      this.livenessCompleted = false;
      this.livenessSuccess = false;

      this.centerSnapshotBlob = null;
      this.centerSnapshotTaken = false;

      if (this.capturedImageUrl) {
        URL.revokeObjectURL(this.capturedImageUrl);
        this.capturedImageUrl = null;
      }

      if (!this.stream || !this.$refs.videoElement) {
        await this.startLivenessFlow()
        return
      }

      // Restart camera stream
      const video = this.$refs.videoElement;
      video.src = '';
      video.srcObject = this.stream;
      await video.play();
    },

    /**
     * Submit recorded video
     */
    submitVideo() {
      const hasBlob = this.videoBlob && this.videoBlob.size
      const hasStoredVideo = this.$store.getters.getField('documentInfo.video.fileName')
      if (!hasBlob && !hasStoredVideo) {
        alert('No video to submit');
        return;
      }

      if (hasBlob) {
        this.$emit('video-recorded', this.videoBlob);
      }

      this.onSubmit();
    },

    /**
     * Handle when the form has been submitted
     *
     * @return {void}
     */
    async onSubmit() {
      let verificationStatus = {
        liveness: {
          score: 100,
          result: 'Real',
          isManual: false,
          createdAt: new Date(),
        },
        face: null,
      }

      this.$store.commit('updateField', {
        key: 'verificationStatus',
        value: verificationStatus,
      })

      if (this.$route.meta.publicRegistration === true) {
        this.$router.push({
          name: 'public-registration-setup-profile-ssa',
          params: {
            dataSegmentId: this.dataSegmentId,
          },
        })
      } else {
        this.$router.push('/ssa-register/setup-profile')
      }
    },

    /**
     * Handle complete post-liveness flow
     * Captures photo, uploads video and photo
     * Safari-safe implementation with proper error handling
     */
    async handlePostLivenessFlow(videoBlob) {
      this.$store.commit('showPageLoader', true);
      this.statusText = 'Processing liveness results...';

      // Detect Safari browser
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      try {
        // Chrome + Safari blob sync fix: Validate blob before proceeding
        if (!videoBlob || !videoBlob.size || videoBlob.size === 0) {
          throw new Error('No video blob available after recording stopped');
        }

        console.log('✓ Video blob ready:', (videoBlob.size / (1024 * 1024)).toFixed(2), 'MB');

        // Step 1: Prepare video blob with Safari-safe format
        const uploadBlob = await this.prepareVideoBlob(videoBlob, isSafari);

        // Validate upload blob
        if (!uploadBlob || !uploadBlob.size) {
          throw new Error('Failed to prepare video blob for upload');
        }

        // Step 2: Upload the auto-captured center-snapshot as the profile picture
        let imageBlob = this.centerSnapshotBlob;
        if (imageBlob && imageBlob.size) {
          const frameFile = new File([imageBlob], 'liveness-picture.jpg', { type: 'image/jpeg' });
          await this.uploadPicture(frameFile);
        }

        // Step 3: Upload video
        this.statusText = 'Uploading video...';
        await this.uploadVideo(uploadBlob);

        // Success - Mark liveness as completed and successful
        this.statusText = '✅ Liveness check passed';
        this.showRetry = false;
        this.livenessCompleted = true;
        this.livenessSuccess = true;
      } catch (err) {
        console.error('❌ Post-liveness flow error:', err);

        // Specific error handling based on failure type
        if (err.message && err.message.includes('No video blob')) {
          this.statusText = '❌ Recording issue - Please retry';
        } else if (err.message && err.message.includes('Photo capture failed')) {
          this.statusText = '❌ Photo capture failed - Please retry';
        } else if (err.message && err.message.includes('upload')) {
          this.statusText = '❌ Upload failed - Please retry';
        } else if (isSafari) {
          this.statusText = '❌ Upload failed. Please ensure camera permissions are granted.';
        } else {
          this.statusText = '❌ Upload failed, please retry';
        }

        this.showRetry = true;
        this.isLivenessPassed = false; // Allow retry

        // Failure - Mark liveness as completed but failed
        this.livenessCompleted = true;
        this.livenessSuccess = false;
      } finally {
        this.$store.commit('showPageLoader', false);
      }
    },

    /**
     * Prepare video blob with Safari-safe format
     * Handles Safari-specific MIME type issues
     * Cross-browser MIME handling - auto-selects best format
     */
    async prepareVideoBlob(videoBlob, isSafari) {
      if (!videoBlob) {
        throw new Error('No video blob available');
      }

      // Safari fix: Use proper MP4 codec specification
      if (isSafari) {
        // Re-create blob with Safari-compatible MIME type
        const safariBlob = new Blob(this.recordedChunks, {
          type: 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
        });

        return safariBlob;
      }

      // Return video blob as-is for other browsers (Chrome, Firefox, etc.)
      return videoBlob;
    },

    /**
     * Upload recorded video to server
     * Safari-safe implementation with proper blob handling
     */
    async uploadVideo(videoBlob) {
      if (!videoBlob) {
        throw new Error('No video blob provided for upload');
      }

      const fileName = 'liveness.mp4';

      // Validate file size (warn if > 25MB)
      if (videoBlob.size > 25 * 1024 * 1024) {
        console.warn('Video file is large (>25MB), upload may be slow');
      }

      // Create FormData with explicit configuration
      const formData = new FormData();
      formData.append('type', 'video');
      formData.append('file', videoBlob, fileName);

      try {
        // Safari-safe axios configuration
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000, // 60 second timeout for large files
          onUploadProgress: (progressEvent) => {
            // progress tracking hook - no UI wired currently
          }
        };

        const { data } = await request.post('files/', formData, config);

        this.$store.commit('updateField', {
          key: 'documentInfo.video.fileName',
          value: data.data.fileName
        });
      } catch (err) {
        console.error('Video upload failed:', err);
        throw new Error('Video upload failed: ' + (err.response.data.message || err.message));
      }
    },

    /**
     * Upload captured photo to server
     * Safari-safe implementation with proper error handling
     */
    async uploadPicture(imageBlob) {
      if (!imageBlob) {
        throw new Error('No image blob provided for upload');
      }

      const fileName = 'liveness-picture.jpg';

      // Create FormData with explicit configuration
      const formData = new FormData();
      formData.append('type', 'customers');
      formData.append('file', imageBlob, fileName);

      try {
        // Safari-safe axios configuration
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000, // 30 second timeout
          onUploadProgress: (progressEvent) => {
            // progress tracking hook - no UI wired currently
          }
        };

        const { data } = await request.post('files/', formData, config);

        this.$store.commit('updateField', {
          key: 'documentInfo.picture.fileName',
          value: data.data.fileName
        });
        this.$store.commit('updateField', {
          key: 'documentInfo.video.thumbnail',
          value: data.data.fileName
        });
        this.capturedImageUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(imageBlob);
        });

        // Set captured image as poster on live video (shows in camera circle)
        if (this.$refs.videoElement) {
          try { this.$refs.videoElement.pause(); } catch (e) {}
          this.$refs.videoElement.setAttribute('poster', this.capturedImageUrl);
        }
      } catch (err) {
        console.error('Picture upload failed:', err);
        throw new Error('Picture upload failed: ' + (err.response.data.message || err.message));
      }
    },

    /**
     * Cleanup resources
     */
    cleanup() {
      // Stop camera stream (from stored stream and video element srcObject if present)
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
      }
      if (this.$refs && this.$refs.videoElement && this.$refs.videoElement.srcObject) {
        try {
          this.$refs.videoElement.srcObject.getTracks().forEach(t => t.stop());
        } catch (e) {
          // ignore errors stopping tracks
        }
        try { this.$refs.videoElement.srcObject = null; } catch (e) {}
      }

      // Stop mirror canvas stream
      if (this.mirrorStream) {
        this.mirrorStream.getTracks().forEach(track => track.stop());
      }

      // Clear intervals
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      if (this.gestureTimerInterval) {
        clearInterval(this.gestureTimerInterval);
      }

      // Clear gesture ignored warning timeout
      if (this.gestureIgnoredTimeout) {
        clearTimeout(this.gestureIgnoredTimeout);
        this.gestureIgnoredTimeout = null;
      }

      // Clear face lost timeout
      this.clearFaceLostTimeout();

      // Stop MediaRecorder
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }

      // Close MediaPipe and mark as not loaded
      try {
        this.faceMesh.close();
      } catch (e) {
        // ignore close errors
      } finally {
        this.faceMesh = null;
        this.faceMeshLoaded = false;
      }

      // Cancel any pending frame callbacks
      if (this.videoFrameCallbackId && this.$refs && this.$refs.videoElement && this.$refs.videoElement.cancelVideoFrameCallback) {
        try {
          this.$refs.videoElement.cancelVideoFrameCallback(this.videoFrameCallbackId);
        } catch (e) {}
        this.videoFrameCallbackId = null;
      }
      if (this.rafId) {
        try { cancelAnimationFrame(this.rafId); } catch (e) {}
        this.rafId = null;
      }

      // Clear mirror canvas references
      this.mirrorCanvas = null;
      this.mirrorCanvasContext = null;
      this.mirrorStream = null;

      // Revoke captured image URL to free memory
      if (this.capturedImageUrl) {
        URL.revokeObjectURL(this.capturedImageUrl);
        this.capturedImageUrl = null;
      }

      // Clean up audio instance
      if (this.successAudio) {
        this.successAudio.pause();
        this.successAudio.src = '';
        this.successAudio = null;
      }

      // Clean up voice instruction audio instances
      if (this.voiceInstructions) {
        Object.values(this.voiceInstructions).forEach(audio => {
          if (audio) {
            audio.pause();
            audio.src = '';
          }
        });
        this.voiceInstructions = {};
      }
    },

    canUseImageCapture(stream) {
      return !!(stream && stream.getVideoTracks && stream.getVideoTracks()[0] && window.ImageCapture);
    },
    async getSnapshot({ stream, liveVideoEl, recordedBlob }) {
      // Tier 1: ImageCapture (Chromium)
      if (this.canUseImageCapture(stream)) {
        try { return await this.captureViaImageCapture(stream); } catch (_) {}
      }
      // Tier 2: Live snapshot (all browsers, including iOS)
      if (liveVideoEl) {
        try { return await this.captureFromLiveVideo(liveVideoEl, 1280); } catch (_) {}
      }
      // Tier 3: (Optional) Recorded blob if small & playable
      if (recordedBlob) {
        try { return await this.captureFromRecordedBlobSafe(recordedBlob, 1.0, 3); } catch (_) {}
      }
      throw new Error('Snapshot failed on all paths');
    },
    async canvasToJpegBlob(canvas, quality = 0.8) {
      if (canvas.convertToBlob) {
        return await canvas.convertToBlob({ type: 'image/jpeg', quality });
      }
      return await new Promise((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', quality);
      });
    },
    async captureViaImageCapture(stream) {
      const track = stream.getVideoTracks()[0];
      const cap = new ImageCapture(track);

      // Prefer takePhoto() (highest quality) if supported; else grabFrame()
      if (cap.takePhoto) {
        const blob = await cap.takePhoto(); // already a Blob (usually JPEG)
        return blob;
      }

      const bitmap = await cap.grabFrame(); // ImageBitmap
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.drawImage(bitmap, 0, 0);
      return await this.canvasToJpegBlob(canvas, 0.8);
    },
    async captureFromLiveVideo(liveVideoEl, maxDim = 1280) {
      // Ensure video is ready
      if (!(liveVideoEl && liveVideoEl.readyState >= 2 && liveVideoEl.videoWidth && liveVideoEl.videoHeight)) {
        await new Promise((resolve, reject) => {
          const to = setTimeout(() => reject(new Error('LIVE_VIDEO_TIMEOUT')), 2500);
          const onCanPlay = () => { clearTimeout(to); resolve(); };
          liveVideoEl.addEventListener('canplay', onCanPlay, { once: true });
        });
      }

      // Clamp size to keep it fast
      let w = liveVideoEl.videoWidth;
      let h = liveVideoEl.videoHeight;
      const ar = w / h;
      if (w > maxDim || h > maxDim) {
        if (w > h) { w = maxDim; h = Math.round(maxDim / ar); }
        else { h = maxDim; w = Math.round(maxDim * ar); }
      }

      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      const canvas = useOffscreen ? new OffscreenCanvas(w, h)
                                  : Object.assign(document.createElement('canvas'), { width: w, height: h });
      const ctx = canvas.getContext('2d', { alpha: false });

      await new Promise(r => requestAnimationFrame(r));
      ctx.drawImage(liveVideoEl, 0, 0, w, h);

      return await this.canvasToJpegBlob(canvas, 0.8);
    },
    async captureFromRecordedBlobSafe(blob, timeInSeconds = 0.5, maxMB = 3) {
      const type = blob.type || '';
      const sizeMB = blob.size ? blob.size / (1024 * 1024) : 0;

      // If big or unplayable, bail
      const testEl = document.createElement('video');
      const playable = !!testEl.canPlayType(type);
      if (!playable || sizeMB >= maxMB) {
        throw new Error('Skip blob decode: unplayable or too large');
      }

      // Minimal, stable version of your original function
      return await new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.src = URL.createObjectURL(blob);

        const cleanup = () => {
          try { URL.revokeObjectURL(video.src); video.removeAttribute('src'); video.load(); } catch {}
        };

        const budget = Math.min(20000, 6000 + Math.round(sizeMB * 3000));
        let t = setTimeout(() => { cleanup(); reject(new Error('Timeout: start')); }, budget);

        const reset = (ms = 6000) => { clearTimeout(t); t = setTimeout(() => { cleanup(); reject(new Error('Timeout')); }, ms); };

        const capture = () => {
          const maxDim = 1280;
          let w = video.videoWidth || 640;
          let h = video.videoHeight || 480;
          const ar = w / h;
          if (w > maxDim || h > maxDim) {
            if (w > h) { w = maxDim; h = Math.round(maxDim / ar); }
            else { h = maxDim; w = Math.round(maxDim * ar); }
          }
          const useOffscreen = typeof OffscreenCanvas !== 'undefined';
          const canvas = useOffscreen ? new OffscreenCanvas(w, h)
                                      : Object.assign(document.createElement('canvas'), { width: w, height: h });
          const ctx = canvas.getContext('2d', { alpha: false });
          requestAnimationFrame(() => {
            try { ctx.drawImage(video, 0, 0, w, h); } catch {}
            this.canvasToJpegBlob(canvas, 0.75)
              .then(b => { clearTimeout(t); cleanup(); b ? resolve(b) : reject(new Error('toBlob null')); })
              .catch(e => { clearTimeout(t); cleanup(); reject(e); });
          });
        };

        const onMeta = async () => {
          reset(8000);
          try { await video.play().catch(()=>{}); video.pause(); await new Promise(r => requestAnimationFrame(r)); } catch {}
          const dur = Number.isFinite(video.duration) ? video.duration : 1;
          const target = Math.max(0.05, Math.min(timeInSeconds, Math.max(dur/2, 0.1), dur - 0.05));
          let done = false;
          const onSeeked = () => { if (done) return; done = true; video.removeEventListener('seeked', onSeeked); capture(); };
          video.addEventListener('seeked', onSeeked, { once: true });
          try { video.currentTime = target; } catch {}
          setTimeout(() => { if (!done) capture(); }, 1500);
        };

        video.addEventListener('loadedmetadata', onMeta, { once: true });
        video.addEventListener('progress', () => reset(8000));
        video.addEventListener('canplay', () => reset(8000));
        video.addEventListener('error', () => { clearTimeout(t); cleanup(); reject(new Error('DECODE_ERROR')); });
      });
    },
  }
}
</script>

<style scoped>
/* Intro layout */
.intro-center {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 24px;
}

.intro-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 16px;
  padding: 28px 22px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  text-align: center;
  margin: 0 auto;
}

.intro-icon {
  font-size: 44px;
  margin: 4px auto 8px auto;
  display: flex;
  justify-content: center;
}

.intro-icon img {
  width: 56px;
  height: 56px;
  display: inline-block;
}

.intro-title {
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  color: #222;
}

.intro-subtitle {
  margin: 8px 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #444;
}

.intro-list {
  list-style: none;
  padding: 0;
  margin: 12px 0 18px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.intro-list-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 1.45;
  color: #555;
}

.intro-list-icon {
  font-size: 16px;
  margin-top: 0;
  position: relative;
  top: -1px; /* subtle nudge up for baseline alignment */
}

.intro-list-icon img {
  width: 18px;
  height: 18px;
  display: inline-block;
}

.intro-volume-note {
  margin-top: 20px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
  border-left: 4px solid #f9a825;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #5d4037;
  text-align: left;
}

.intro-cta {
  margin-top: 28px;
  padding-left: 24px;
  padding-right: 24px;
}

@media (max-width: 768px) {
  .intro-center {
    padding: 16px;
    min-height: 100vh;
  }
  .intro-card {
    padding: 24px 18px;
    border-radius: 14px;
  }
  .intro-title {
    font-size: 20px;
  }
  .intro-list-item {
    font-size: 13px;
  }
}

/* UI: Unified instruction prompt area above camera */
.instruction-prompt-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-height: 60px;
  justify-content: center;
}

.instruction-prompt {
  color: #666;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  animation: fadeInDown 0.3s ease;
}

.instruction-prompt.error {
  background: rgba(239, 68, 68, 0.9);
  color: white;
}

.instruction-prompt.success {
  background: rgba(0, 200, 83, 0.9);
  color: white;
}

.distance-badge {
  background: rgba(0, 200, 83, 0.9);
  color: white;
  padding: 6px 16px;
  border-radius: 18px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 200, 83, 0.4);
  animation: fadeInUp 0.3s ease;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* UI: Main container - centered vertically and horizontally */
.liveness-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #fafafa;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  padding: 20px;
}

/* UI: Center content wrapper */
.liveness-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 500px;
  width: 100%;
}

/* UI: Gesture bubble styling - floating label above camera */
.gesture-label {
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  animation: fadeInDown 0.4s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* UI: Circular camera preview mask */
.camera-circle-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.camera-circle {
  width: 320px;
  height: 320px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  border: 4px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  background: #000;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

/* Dynamic border feedback for positioning */
.camera-circle.border-perfect {
  border-color: #00c853;
  box-shadow: 0 0 25px rgba(0, 200, 83, 0.8), 0 8px 32px rgba(0, 0, 0, 0.12);
  /* Steady green border when face is perfectly positioned - no animation */
}

.camera-circle.border-error {
  border-color: #ef4444;
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.6), 0 8px 32px rgba(0, 0, 0, 0.12);
  /* Pulsating red border when face is out of position */
  animation: pulseError 0.8s ease-in-out infinite;
}

.camera-circle.border-neutral {
  border-color: #fbbf24;
  box-shadow: 0 0 15px rgba(251, 191, 36, 0.5), 0 8px 32px rgba(0, 0, 0, 0.12);
  /* Soft yellow border for initialization/paused state */
  animation: pulseNeutral 2s ease-in-out infinite;
}

@keyframes pulseError {
  0%, 100% {
    box-shadow: 0 0 20px rgba(239, 68, 68, 0.6), 0 8px 32px rgba(0, 0, 0, 0.12);
  }
  50% {
    box-shadow: 0 0 30px rgba(239, 68, 68, 0.8), 0 8px 32px rgba(0, 0, 0, 0.12);
  }
}

@keyframes pulseNeutral {
  0%, 100% {
    box-shadow: 0 0 15px rgba(251, 191, 36, 0.5), 0 8px 32px rgba(0, 0, 0, 0.12);
  }
  50% {
    box-shadow: 0 0 25px rgba(251, 191, 36, 0.7), 0 8px 32px rgba(0, 0, 0, 0.12);
  }
}

.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Mirror Effect - Selfie Camera Style */
.video-preview.mirrored {
  transform: scaleX(-1);
  -webkit-transform: scaleX(-1);
}

/* Post-Liveness Result State */
.liveness-result {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000; /* Default: black overlay for failure */
  transition: opacity 0.3s ease;
}

.liveness-result.success {
  background-color: transparent; /* Success: show captured image */
  overflow: hidden;
}

.liveness-result.failure {
  background-color: #000; /* Failure: solid black circle */
}

.cropped-face {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* UI: Loader overlay during upload */
.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 50%;
  z-index: 20;
}

.modern-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loader-text {
  color: #333;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  max-width: 200px;
}

/* Success checkmark */
.success-checkmark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

/* Gesture progress dots */
.gesture-dots {
  display: flex;
  gap: 8px;
  align-items: center;
}

.gesture-dots .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e0e0e0;
  transition: all 0.3s ease;
}

.gesture-dots .dot.active {
  background: #667eea;
  transform: scale(1.3);
}

.gesture-dots .dot.completed {
  background: #00c853;
}

/* UI: Progress bar below camera - modern gradient style */
.progress-bar-wrapper {
  width: 280px;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress {
  height: 100%;
  background: linear-gradient(90deg, #00c853, #b2ff59);
  transition: width 0.3s ease;
  border-radius: 3px;
}

.progress-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

/* Status messages */
.status-message {
  font-size: 15px;
  color: #666;
  text-align: center;
  margin: 0;
  font-weight: 500;
}

.status-message.success {
  color: #00c853;
  font-weight: 600;
}

.status-message.error {
  color: #f44336;
  font-weight: 600;
}

/* Action buttons - modern minimal style */
.action-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Responsive camera layout - fully mobile responsive */
@media (max-width: 768px) {
  .liveness-container {
    padding: 16px;
    min-height: 100vh;
  }

  .liveness-center {
    gap: 20px;
    width: 100%;
    max-width: 100%;
  }

  /* Responsive instruction prompts */
  .instruction-prompt-area {
    margin-bottom: 12px;
    min-height: 45px;
  }

  .instruction-prompt {
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 20px;
    max-width: 98%;
  }

  .distance-badge {
    font-size: 12px;
    padding: 5px 14px;
    border-radius: 16px;
  }

  /* Responsive camera layout - scales with screen */
  .camera-circle {
    width: min(320px, 85vw);
    height: min(320px, 85vw);
    border-width: 3px;
  }

  /* Gesture UI for mobile view - positioned above camera */
  .gesture-label {
    font-size: 15px;
    padding: 10px 20px;
    max-width: 90%;
    word-wrap: break-word;
  }

  .gesture-dots .dot {
    width: 8px;
    height: 8px;
  }

  /* Responsive progress bar */
  .progress-bar-wrapper {
    width: min(280px, 85vw);
  }

  .progress-bar {
    height: 5px;
  }

  .progress-text {
    font-size: 12px;
    margin-top: 6px;
  }

  /* Mobile buttons - full width on small screens */
  .action-buttons {
    width: 100%;
    flex-direction: column;
    gap: 12px;
  }

  .btn {
    width: 100%;
    max-width: 300px;
    padding: 14px 24px;
    font-size: 15px;
  }

  .status-message {
    font-size: 14px;
    padding: 0 16px;
  }

  /* Loader adjustments for mobile */
  .modern-spinner {
    width: 45px;
    height: 45px;
  }

  .loader-text {
    font-size: 13px;
    max-width: 180px;
  }
}

/* Extra small screens - prevent mobile Safari zoom */
@media (max-width: 480px) {
  .camera-circle {
    width: min(280px, 90vw);
    height: min(280px, 90vw);
  }

  .gesture-label {
    font-size: 14px;
    padding: 8px 16px;
  }

  /* Extra small instruction prompts */
  .instruction-prompt-area {
    margin-bottom: 12px;
    min-height: 45px;
  }

  .instruction-prompt {
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 18px;
    max-width: 98%;
  }

  .distance-badge {
    font-size: 11px;
    padding: 4px 12px;
    border-radius: 14px;
  }

  .btn {
    font-size: 14px;
    padding: 12px 20px;
  }

  .status-message {
    font-size: 13px;
  }
}

/* Safari-specific fixes - hardware acceleration & zoom prevention */
@supports (-webkit-appearance:none) {
  .video-preview,
  .camera-circle {
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
}

/* Prevent mobile Safari zoom on double tap */
* {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

/* Ensure buttons and text don't trigger zoom */
button,
.btn {
  touch-action: manipulation;
  -webkit-user-select: none;
  user-select: none;
}
</style>
